import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReguaSegment } from '../../interfaces/quick-approval.interface';

@Component({
  selector: 'app-slider',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './slider.html',
  styleUrls: ['./slider.scss'],
})
export class Slider implements OnInit, OnChanges {
  @Input() value: number = 0;
  @Input() segments: ReguaSegment[] = [];
  @Output() valueChange = new EventEmitter<number>();

  isReadOnly = true;

  @ViewChild('sliderWrapper', { static: true })
  private sliderWrapper!: ElementRef<HTMLElement>;

  majorSteps = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];
  processedSegments: Array<{ width: string; color: string; label: string }> = [];
  isDragging = false;
  dragValue = 0;

  private activePointerId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['segments']) {
      this.refreshSegments();
    }
  }

  ngOnInit(): void {
    this.refreshSegments();
  }

  getThumbPosition(): string {
    const value = this.isDragging ? this.dragValue : this.value;
    return `${(this.clampValue(value) / 90) * 100}%`;
  }

  onThumbPointerDown(event: PointerEvent): void {
    if (this.isReadOnly || event.button !== 0 || this.activePointerId !== null) {
      return;
    }

    event.preventDefault();
    this.isDragging = true;
    this.activePointerId = event.pointerId;
    this.dragValue = this.value;

    const thumb = event.currentTarget;
    if (thumb instanceof HTMLElement) {
      thumb.setPointerCapture(event.pointerId);
    }

    this.updateDragValue(event);
  }

  onThumbPointerMove(event: PointerEvent): void {
    if (!this.isReadOnly && this.activePointerId === event.pointerId) {
      this.updateDragValue(event);
    }
  }

  onThumbPointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }

    if (this.isReadOnly) {
      this.isDragging = false;
      this.activePointerId = null;
      return;
    }

    this.updateDragValue(event);
    const value = this.dragValue;
    this.isDragging = false;
    this.activePointerId = null;

    if (value !== this.value) {
      this.valueChange.emit(value);
    }
  }

  onThumbPointerCancel(event: PointerEvent): void {
    if (this.activePointerId === event.pointerId) {
      this.isDragging = false;
      this.activePointerId = null;
    }
  }

  onThumbKeyDown(event: KeyboardEvent): void {
    if (this.isReadOnly) {
      return;
    }

    const increments: Record<string, number> = {
      ArrowRight: 1,
      ArrowUp: 1,
      ArrowLeft: -1,
      ArrowDown: -1,
      PageUp: 10,
      PageDown: -10,
      Home: -90,
      End: 90,
    };
    const increment = increments[event.key];

    if (increment === undefined) {
      return;
    }

    event.preventDefault();
    const value = this.clampValue(this.value + increment);
    if (value !== this.value) {
      this.valueChange.emit(value);
    }
  }

  getStepPosition(step: number): string {
    return `${(step / 90) * 100}%`;
  }

  /**
   * Calcula a posição do label baseado no segmento
   */
  getLabelPosition(index: number): string {
    if (!this.segments || this.segments.length === 0 || index >= this.segments.length) {
      // Posições padrão
      const defaultPositions = ['0%', '44%', '78%'];
      return defaultPositions[index] || '50%';
    }

    const segment = this.segments[index];
    const startPercent = this.normalizePercent(segment.startPercent, 0);
    const endPercent = this.normalizePercent(segment.endPercent, 90);
    const middlePercent = (startPercent + endPercent) / 2;
    const position = (middlePercent / 90) * 100;
    return `${position}%`;
  }

  /**
   * Processa os segmentos recebidos da API para exibição na régua
   */
  private processSegments(): void {
    this.processedSegments = this.segments.map((segment) => {
      const startPercent = this.normalizePercent(segment.startPercent, 0);
      const endPercent = this.normalizePercent(segment.endPercent, 90);

      const width = ((endPercent - startPercent) / 90) * 100;
      return {
        width: `${width}%`,
        color: segment.color,
        label: this.getStatusLabel(segment.status),
      };
    });
  }

  private normalizePercent(value: number, fallback: number): number {
    return Number.isInteger(value) ? value : fallback;
  }

  /**
   * Define segmentos padrão quando não há dados da API
   */
  private setDefaultSegments(): void {
    this.processedSegments = [
      { width: '22.2%', color: '#96A3A8', label: '' }, // Cinza 0-20
      { width: '44.4%', color: '#EA9A3B', label: 'Sujeito a aprovação' }, // Laranja 20-60
      { width: '33.4%', color: '#00B0F0', label: 'Crédito Pré-aprovado' }, // Azul 60-90
    ];
  }

  /**
   * Retorna o label baseado no status
   */
  private getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      Rejected: '',
      Credit_Officer_Decision: 'Sujeito a aprovação',
      Approved: 'Crédito Pré-aprovado',
    };
    return labels[status] || '';
  }

  private updateDragValue(event: PointerEvent): void {
    const bounds = this.sliderWrapper.nativeElement.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }

    const position = (event.clientX - bounds.left) / bounds.width;
    this.dragValue = this.clampValue(position * 90);
  }

  private clampValue(value: number): number {
    return Math.min(90, Math.max(0, value));
  }

  private refreshSegments(): void {
    if (this.segments && this.segments.length > 0) {
      this.processSegments();
    } else {
      this.setDefaultSegments();
    }
  }
}
